<template>
    <div class="layout-live-chat flex h-[calc(36vh)] flex-col lg:h-full bg-zinc-950 border border-black" v-if="connected">
        <div
            class="cursor-pointer bg-yellow-500"
            :class="{'hidden': !manualScroll}"
            @click="resumeAutoScroll"
        >
            Click to resume auto-scrolling
        </div>
        <div
            ref="chatContainer"
            class="scroll-container h-20 flex-grow overflow-y-scroll"
            @scroll="setScrollManual"
        >
            <template v-for="message in messages">
                <div
                    v-if="message.data.Sender.UserId !== 'Anon'"
                    :key="message.id"
                    class="m-2 w-fit px-2 text-sm text-zinc-300"
                    style="font-size: 0.92rem; line-height: 1.3rem;"
                >
                    <span class="font-semibold" :style="{'color': `hsl(${getTextHue(message.data.Sender.UserId)}, 100%, 80%)`}">{{ message.user }}:</span>
                    {{ message.message }}
                </div>
            </template>
            <div ref="scrollTarget"></div>
        </div>
        <div class="flex w-full" @keydown.enter="handleSend">
            <FormFieldLiveChat
                field="message"
                class="mx-2.5 mb-0.5 flex-grow rounded-md"
                :value="message"
                @update="updateMessageField"
                placeholder="Chat..."
            ></FormFieldLiveChat>
            <button
                class="mt-2.5 mb-0.5 mr-2.5 flex-shrink-0 flex-grow-0 cursor-pointer rounded-md bg-sky-400 text-zinc-900 hover:bg-sky-300 focus-visible:outline-zinc-600 px-3.5"
                @click.stop="handleSend"
                :disabled="!message"
            >
                Send
            </button>
        </div>
        <LayoutDialog title="Sign in to your Andy Guitar account" v-if="dialog">
            <template v-slot:content>
                <div class="mb-8 text-lg">
                    It's quick, easy and free
                </div>
                <div class="mb-8 flex flex-row items-center space-x-2">
                    <ButtonPrimary
                        buttonRoute="/login"
                        buttonText="Login"
                    ></ButtonPrimary>
                    <div class="text-lg">
                        or
                        <NuxtLink class="text-sky-400 hover:text-sky-300 font-semibold underline" to="/signup">Signup</NuxtLink>
                    </div>
                </div>
                <div>
                    <button class="text-zinc-200 hover:text-zinc-100 font-semibold underline" @click="closeDialog()">
                        Maybe Later
                    </button>
                </div>
            </template>
        </LayoutDialog>
    </div>
</template>

<script>
import { mapState } from 'pinia';

export default {
    name: 'LayoutLiveChat',
    props: {
        token: {
            required: true,
            type: String,
        },
        streamSlug: {
            required: true,
            type: String,
        },
        connected: {
            required: false,
            type: Boolean,
            default: false,
        },
        messages: {
            required: false,
            type: Array,
            default () {
                return [];
            },
        },
        message: {
            required: false,
            type: String,
            default: '',
        },
        initChat: {
            required: false,
            type: Function,
            default: () => {},
        },
        updateMessageField: {
            required: false,
            type: Function,
            default: () => {},
        },
        sendMessage: {
            required: false,
            type: Function,
            default: () => {},
        },
    },
    data () {
        return {
            dialog: false,
            manualScroll: false,
            autoScrolling: false,
        };
    },
    computed: {
        ...mapState(useStoreAuth, {
            isLoggedIn: 'isLoggedIn',
        }),
        messagesLength () {
            return this.messages.length;
        }
    },
    methods: {
        handleSend () {
            if (this.isLoggedIn) return this.sendMessage();
            return this.openDialog();
        },
        openDialog () {
            this.dialog = true;
            this.$emit('opened');
        },
        closeDialog () {
            this.dialog = false;
            this.$emit('cancelled');
        },
        setScrollManual () {
            if (this.autoScrolling) {
                this.autoScrolling = false;
                return;
            }
            this.manualScroll = true;
        },
        resumeAutoScroll () {
            const target = this.$refs.scrollTarget;
            window.target = target;
            target.parentElement.scrollTo(0, target.offsetTop);
            this.autoScrolling = true;
            this.manualScroll = false;
        },
        getTextHue (seed) {
            let hash = 0;
            for (let i = 0; i < seed.length; i++) {
                hash = seed.charCodeAt(i) + ((hash << 5) - hash);
            }
            return (hash & 0x00FFFFFF) % 360;
        },
    },
    watch: {
        token (newVal, oldVal) {
            if (newVal !== oldVal) this.initChat({token: this.token, streamSlug: this.streamSlug});
        },
        messagesLength () {
            if (this.manualScroll) return;
            this.$nextTick(() => {
                this.resumeAutoScroll();
            });
        },
    },
    mounted () {
        this.initChat({token: this.token, streamSlug: this.streamSlug}); // Init sets up a web socket connection to be used by the client, so shouldn't be done in fetch()
    },
};
</script>
